import { UserPlus } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/_shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/_shared/components/ui/dialog';
import { Input } from '@/_shared/components/ui/input';
import { Label } from '@/_shared/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/_shared/components/ui/select';

import type { LookoutRole } from './InviteOrganizationMemberDialog';

interface SiblingInviteDialogProps {
  open: boolean;
  pending: boolean;
  onSubmit: (input: { email: string; role: LookoutRole }) => void;
  onClose: () => void;
}

/**
 * Invite (LLD §5.2). For a person outside the organization, RudderStack emails a link to Lookout: they join the
 * organization and this workspace's RudderStack workspace as a member with no grants, and this workspace with the role
 * picked here. For a member of the organization, the Users tab asks to send a Lookout invite instead.
 */
export function SiblingInviteDialog({
  open,
  pending,
  onSubmit,
  onClose,
}: SiblingInviteDialogProps): React.ReactElement {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<LookoutRole>('member');

  const close = (): void => {
    setEmail('');
    setRole('member');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !pending) close();
      }}
    >
      <DialogContent>
        <form
          noValidate
          aria-label="Invite member"
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            const trimmed = email.trim();
            if (trimmed) onSubmit({ email: trimmed, role });
          }}
        >
          <DialogHeader>
            <DialogTitle>Invite member</DialogTitle>
            <DialogDescription>
              A person outside your RudderStack organization gets an invitation from RudderStack. A member of the
              organization gets an invite from Lookout.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            <Label htmlFor="sibling-invite-email">Email address</Label>
            <Input
              id="sibling-invite-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="colleague@company.com"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="sibling-invite-role">Role in Lookout</Label>
            <Select value={role} onValueChange={(value) => setRole(value as LookoutRole)}>
              <SelectTrigger id="sibling-invite-role" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="member">Member</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" disabled={pending} onClick={close} label="Cancel" />
            <Button
              type="submit"
              variant="primary"
              Icon={<UserPlus />}
              loading={pending}
              disabled={!email.trim()}
              label="Send invite"
            />
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
